import React from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * RoleGuidedTour
 *
 * Shepherd-style tour for each role. **Never auto-pops** — only runs when
 * the user clicks "Restart guided tour" in the Help menu. That action fires
 * a `ievntur:start-tour` DOM event which this component listens for.
 *
 * We keep the localStorage keys defined so any existing values don't leak,
 * but they no longer gate auto-run. Removing the auto-popup was a direct
 * user request (disruptive on every login / re-focus / cold start).
 */
const STORAGE_KEYS = {
  admin: 'ievntur_admin_tour_v1',
  super_admin: 'ievntur_admin_tour_v1',
  talent: 'ievntur_talent_tour_v1',
  client: 'ievntur_client_tour_v1',
};

export const TOUR_START_EVENT = 'ievntur:start-tour';

const ADMIN_HOME_STEPS = [
  {
    target: 'body',
    placement: 'center',
    title: 'Welcome to the Admin Console',
    content: "A quick 60-second tour covering the tools you'll use every day — Shifts, Broadcast, IDV, Attendance.",
  },
  {
    target: '[data-testid="geocode-coverage-chip"]',
    title: 'Geocode coverage',
    content: 'Live percentage of talent profiles with geocoded locations. Radius-based targeting relies on this.',
  },
  {
    target: '[data-testid="help-menu-btn"]',
    title: 'Need help?',
    content: 'Restart this tour, see What\'s New, or jump to docs any time from the Help menu.',
  },
];

const TALENT_HOME_STEPS = [
  {
    target: 'body',
    placement: 'center',
    title: 'Welcome to iEvntür',
    content: "30-second tour. We'll show you where to find gigs, share your booking link, and keep your profile ready.",
  },
  {
    target: '[data-testid="talent-greeting"]',
    title: 'Your home base',
    content: 'This feed surfaces gigs you can apply to, plus updates from our team. Check in daily.',
  },
  {
    target: '[data-testid="my-booking-link-card"]',
    title: 'Your personal booking link',
    content: 'Share this anywhere you get hired — IG bio, TikTok, email. Clients book you through iEvntür and we handle the rest.',
  },
  {
    target: '[data-testid="copy-link-btn"]',
    title: 'One-tap copy',
    content: 'Copy your link here, then paste it into your IG bio or anywhere your clients can find you.',
  },
  {
    target: '[data-testid="create-post-trigger"]',
    title: 'Post to the feed',
    content: 'Share highlights or availability with the community — posts help you get remembered.',
  },
];

const CLIENT_HOME_STEPS = [
  {
    target: 'body',
    placement: 'center',
    title: 'Welcome to iEvntür',
    content: "Post jobs, browse talent, and manage bookings. Here's the 30-second tour.",
  },
  {
    target: '[data-testid="client-greeting"]',
    title: 'Your dashboard',
    content: 'Your shifts, invoices, and recent activity all live here. Scroll for the feed and quick actions.',
  },
  {
    target: '[data-testid="post-gig-btn"]',
    title: 'Post a gig',
    content: 'Create a new shift in under a minute. We\'ll match it with the right talent automatically.',
  },
  {
    target: '[data-testid="view-invoices-btn"]',
    title: 'Invoices & billing',
    content: 'Track what\'s billed, paid, and pending. Export receipts any time.',
  },
];

const PAGE_STEPS = {
  admin: { '/admin': ADMIN_HOME_STEPS, '/admin/home': ADMIN_HOME_STEPS },
  super_admin: { '/admin': ADMIN_HOME_STEPS, '/admin/home': ADMIN_HOME_STEPS },
  talent: { '/talent': TALENT_HOME_STEPS, '/talent/home': TALENT_HOME_STEPS },
  client: { '/client': CLIENT_HOME_STEPS, '/client/home': CLIENT_HOME_STEPS },
};

const RoleGuidedTour = () => {
  const { user } = useAuth() || {};
  const location = useLocation();
  const [run, setRun] = React.useState(false);
  const [steps, setSteps] = React.useState([]);

  // Listen for the explicit "start tour" event dispatched by HelpMenu. This
  // replaces the previous effect that auto-started when a localStorage flag
  // was missing — users found that disruptive on every login / cold start.
  React.useEffect(() => {
    const onStart = () => {
      const role = user?.role;
      if (!role) return;
      const map = PAGE_STEPS[role] || {};
      // Prefer page-specific steps; fall back to the role's home steps so
      // the tour is always available no matter where the user triggers it.
      const homePath = {
        admin: '/admin/home',
        super_admin: '/admin/home',
        talent: '/talent/home',
        client: '/client/home',
      }[role];
      const pageSteps = map[location.pathname] || (homePath && map[homePath]) || [];
      if (pageSteps.length === 0) return;
      setSteps(pageSteps);
      // Tiny delay so Joyride can find the live DOM targets.
      setTimeout(() => setRun(true), 150);
    };
    window.addEventListener(TOUR_START_EVENT, onStart);
    return () => window.removeEventListener(TOUR_START_EVENT, onStart);
  }, [user?.role, location.pathname]);

  const handleCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Remember completion — keeps legacy behaviour (we no longer gate on
      // this, but Help > "Restart guided tour" + manual events still work).
      const key = STORAGE_KEYS[user?.role];
      if (key) localStorage.setItem(key, '1');
      setRun(false);
    }
  };

  if (!run || steps.length === 0) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      disableScrolling={false}
      callback={handleCallback}
      styles={{
        options: { primaryColor: '#06b6d4', textColor: '#0f172a', zIndex: 10000, arrowColor: '#fff', backgroundColor: '#fff' },
        buttonNext: { backgroundColor: '#06b6d4', borderRadius: 6 },
        buttonBack: { color: '#64748b' },
        buttonSkip: { color: '#ef4444' },
      }}
    />
  );
};

export default RoleGuidedTour;
